import React, {createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';
import {
    DocumentDeleteResults,
    DocumentRead,
    DocumentReadWReferences,
    DocumentReadWRefs,
    DocumentSearch,
    UserMembership,
    UserRead
} from "../schemas";
import {getDocument as getDocumentApi, searchDocuments, deleteDocuments as deleteDocumentsApi, fetchOrStoreUser} from "../api";
import Swal from "sweetalert2";
import {ThemeColors} from "../colors";
import {useGroupContext} from "./group_context";
import {useFolderContext} from "./folder_context";
import {
    DOCUMENT_UPLOAD_COMPLETED,
    DocumentUploadCompletedEventDetail,
    TAG_UPDATE_COMPLETED,
    TagUpdatedEventDetail
} from "../events";
import {withApiErrorHandler, withApiErrorHandlerThrow} from "../error_handling";

export interface EnhancedDocumentsContextType {
    docLoading: boolean;
    setDocLoading: React.Dispatch<React.SetStateAction<boolean>>;

    documentSearch: DocumentSearch | null
    updateDocumentSearch: (updates: Partial<DocumentSearch>) => Promise<DocumentSearch>;
    pullDocuments: (searchParams: DocumentSearch) => Promise<Map<string, DocumentReadWRefs>>;
    refreshDocumentField: <K extends keyof DocumentRead>(
        doc: DocumentRead | DocumentReadWReferences,
        field: K
    ) => Promise<DocumentReadWReferences>;
    getDocument: (id: string) => Promise<DocumentReadWReferences>;

    loadedDocMap: Map<string, DocumentReadWRefs>;

    docTotalCount: number;

    docIdsOrdered: string[];
    setDocIdsOrdered: React.Dispatch<React.SetStateAction<string[]>>;

    selectedDocIds: string[];
    setSelectedDocIds: React.Dispatch<React.SetStateAction<string[]>>;

    deleteDocuments: (docIds: string[]) => void;
    document: DocumentReadWReferences | null | undefined;
    setDocument: (document: DocumentReadWReferences | null | undefined) => void;

    lastVisitedDocument: DocumentReadWReferences | null;
    setLastVisitedDocument: React.Dispatch<React.SetStateAction<DocumentReadWReferences | null>>;

    // Simplified prev/next - just IDs, with navigation functions
    prevDocId: string | null;
    nextDocId: string | null;
    canGoPrev: boolean;
    canGoNext: boolean;
    goToPrevDocument: () => Promise<void>;
    goToNextDocument: () => Promise<void>;
    navLoading: boolean;

}

export const EnhancedDocumentsContext = createContext<EnhancedDocumentsContextType | undefined>(undefined);

interface EnhancedDocumentsProviderProps {
    children: ReactNode;
}

export const EnhancedDocumentsProvider: React.FC<EnhancedDocumentsProviderProps> = ({children}) => {
    const {
        currentGroup,
    } = useGroupContext()!;
    const {currentFolder} = useFolderContext()!;


    const [docLoading, setDocLoading] = useState(false);

    const [documentSearch, _setDocumentSearch] = useState<DocumentSearch | null>(null)

    // Ref to track current documentSearch for event handlers (avoids stale closure issue)
    const documentSearchRef = useRef<DocumentSearch | null>(null);
    useEffect(() => {
        documentSearchRef.current = documentSearch;
    }, [documentSearch]);

    const [selectedDocIds, setSelectedDocIds] = useState<string[]>([]);

    const [docIdsOrdered, setDocIdsOrdered] = useState<string[]>([]);
    const [loadedDocMap, setLoadedDocMap] = useState<Map<string, DocumentRead>>(new Map());
    const [docTotalCount, setDocTotalCount] = useState<number>(0);

    const [document, _setDocument] = useState<DocumentRead | null | undefined>(undefined);
    const [lastVisitedDocument, setLastVisitedDocument] = useState<DocumentRead | null>(null);

    // Simplified navigation
    const [prevDocId, setPrevDocId] = useState<string | null>(null);
    const [nextDocId, setNextDocId] = useState<string | null>(null);
    const [navLoading, setNavLoading] = useState(false);

    // Ref to track navigation version - used to cancel stale requests
    const navVersionRef = useRef(0);

    // When navigating across page boundaries, this ref stores which end of the
    // new page we should auto-select: 'first' (next page) or 'last' (prev page).
    const pendingNavDirection = useRef<'first' | 'last' | null>(null);


    const getDocument = useCallback(async (id: string) => {
        console.log('Getting document with ID:', id);
            if (loadedDocMap.has(id)) {
                return loadedDocMap.get(id)!;
            }
            const doc = await withApiErrorHandlerThrow<DocumentReadWReferences>(
                () => getDocumentApi(id),
                {titleQualifier: "Error Fetching Document"});
            console.log('Fetched document:', doc);
            return doc;
        },
        []
    )

    useEffect(() => {
        // Clear loaded documents when group changes to prevent stale data
        setLoadedDocMap(new Map());
        setDocIdsOrdered([]);
        setDocTotalCount(0);
        setDocument(null);
        updateDocumentSearch({group_id: currentGroup?.id, search_origin: "nav"})
    }, [currentGroup?.id]);

    useEffect(() => {
        setDocument(null)
        setDocTotalCount(0);
        setDocIdsOrdered([]);
        setLoadedDocMap(new Map());
        updateDocumentSearch({
            folder_tag_id: currentFolder?.id,
            search_origin: "nav",
        });
    }, [currentFolder]);

    useEffect(() => {
        const onDocumentUploadCompleted = (event: CustomEvent<DocumentUploadCompletedEventDetail>) => {
            console.log('Document upload completed:', event.detail);
            // Use ref to get current documentSearch (avoids stale closure)
            _setDocumentSearch({...documentSearchRef.current, search_origin: "nav"});
        };
        window.addEventListener(DOCUMENT_UPLOAD_COMPLETED, onDocumentUploadCompleted as EventListener);
        return () => window.removeEventListener(DOCUMENT_UPLOAD_COMPLETED, onDocumentUploadCompleted as EventListener)
    }, [])

    useEffect(() => {
        const onTagUpdated = (event: CustomEvent<TagUpdatedEventDetail>) => {
            // Check for tag in our current list then update and set
            // TODO Optimize with selective column refresh
            console.log('Tag update completed:', event.detail);
            // Use ref to get current documentSearch (avoids stale closure)
            _setDocumentSearch({...documentSearchRef.current, search_origin: "nav"});
        };
        window.addEventListener(TAG_UPDATE_COMPLETED, onTagUpdated as EventListener);
        return () => window.removeEventListener(TAG_UPDATE_COMPLETED, onTagUpdated as EventListener)
    }, [])

    // YOUR COMPONENT SHOULD CONTROL WHEN TO CALL THIS FUNCTION
    const pullDocuments = useCallback(
        async (searchParams: DocumentSearch): Promise<Map<string, DocumentReadWRefs>> => {
            setDocLoading(true);
            try {
                const {docs, totalCount} = await withApiErrorHandlerThrow<{
                    docs: DocumentReadWRefs[];
                    totalCount: number
                }>(
                    () => searchDocuments(searchParams),
                    {titleQualifier: "Error Fetching Documents"}
                );
                const map = new Map<string, DocumentReadWRefs>();
                docs.forEach((d) => map.set(d.id, d));
                setLoadedDocMap(map);
                setDocTotalCount(totalCount);
                setDocIdsOrdered(docs.map((d) => d.id));
                return map;
            } finally {
                setDocLoading(false);
            }
        },
        []
    );

    const refreshDocumentField = useCallback(
        async <K extends keyof DocumentRead>(
            doc: DocumentRead | DocumentReadWReferences,
            field: K
        ): Promise<DocumentReadWReferences> => {
            const refreshed = await withApiErrorHandlerThrow<DocumentReadWReferences>(
                () => getDocumentApi(doc.id),
                {titleQualifier: `Error refreshing document ${String(field)}`}
            );

            setLoadedDocMap(prev => {
                const next = new Map(prev);
                next.set(refreshed.id, refreshed);
                return next;
            });

            if (document?.id === refreshed.id) {
                setDocument(refreshed);
            }

            return refreshed;
        },
        [document]
    );

    const updateDocumentSearch = useCallback(
        async (updates: Partial<DocumentSearch>): Promise<DocumentSearch> => {
            const merged = {
                ...documentSearch!,
                ...updates,
            };
            _setDocumentSearch(merged);
            return merged;
        },
        [documentSearch]
    );

    // When a cross-page navigation completes, auto-select the first or last doc
    useEffect(() => {
        if (!pendingNavDirection.current || docIdsOrdered.length === 0) return;

        const direction = pendingNavDirection.current;
        pendingNavDirection.current = null;

        const targetId = direction === 'first'
            ? docIdsOrdered[0]
            : docIdsOrdered[docIdsOrdered.length - 1];

        if (!targetId) return;

        // Fetch full document and set it as current
        (async () => {
            try {
                const doc = await withApiErrorHandlerThrow<DocumentReadWReferences>(
                    () => getDocumentApi(targetId),
                    {titleQualifier: "Error Fetching Document"}
                );
                _setDocument(doc);
            } finally {
                setNavLoading(false);
            }
        })();
    }, [docIdsOrdered]);

    // Calculate prev/next IDs whenever document or docIdsOrdered changes
    useEffect(() => {
        if (!document || docIdsOrdered.length === 0) {
            setPrevDocId(null);
            setNextDocId(null);
            return;
        }

        const currentIndex = docIdsOrdered.indexOf(document.id);
        if (currentIndex === -1) {
            setPrevDocId(null);
            setNextDocId(null);
            return;
        }

        // Calculate prev ID (null if at start, unless we have more pages to load)
        const prevIndex = currentIndex - 1;
        const currentOffset = documentSearch?.offset ?? 0;
        if (prevIndex >= 0) {
            setPrevDocId(docIdsOrdered[prevIndex]);
        } else if (currentOffset > 0) {
            // There are previous pages we could load
            setPrevDocId('__LOAD_PREV_PAGE__');
        } else {
            setPrevDocId(null);
        }

        // Calculate next ID (null if at end, unless we have more pages to load)
        const nextIndex = currentIndex + 1;
        if (nextIndex < docIdsOrdered.length) {
            setNextDocId(docIdsOrdered[nextIndex]);
        } else if (currentOffset + docIdsOrdered.length < docTotalCount) {
            // There are more pages to load
            setNextDocId('__LOAD_NEXT_PAGE__');
        } else {
            setNextDocId(null);
        }
    }, [document?.id, docIdsOrdered, docTotalCount, documentSearch?.offset, documentSearch?.limit]);

    const setDocument = (doc: DocumentReadWReferences | null | undefined) => {
        _setDocument(doc);
    }

    // Navigate to previous document
    const goToPrevDocument = useCallback(async () => {
        if (!prevDocId || navLoading) return;

        const currentNavVersion = ++navVersionRef.current;
        setNavLoading(true);

        try {
            if (prevDocId === '__LOAD_PREV_PAGE__') {
                // Need to load previous page — select last doc when it arrives
                pendingNavDirection.current = 'last';
                const currentOffset = documentSearch?.offset ?? 0;
                const currentLimit = documentSearch?.limit ?? 50;
                const newOffset = Math.max(0, currentOffset - currentLimit);

                const newSearch = await updateDocumentSearch({
                    offset: newOffset,
                    search_origin: "nav"
                });
                await pullDocuments(newSearch);
                // Don't setNavLoading(false) here — the pending effect handles it
                return;
            } else {
                // Navigate to the prev document in current list
                if (currentNavVersion !== navVersionRef.current) return; // Stale request

                const doc = await withApiErrorHandlerThrow<DocumentReadWReferences>(
                    () => getDocumentApi(prevDocId),
                    {titleQualifier: "Error Fetching Document"}
                );

                if (currentNavVersion !== navVersionRef.current) return; // Stale request
                _setDocument(doc);
            }
        } finally {
            if (currentNavVersion === navVersionRef.current && !pendingNavDirection.current) {
                setNavLoading(false);
            }
        }
    }, [prevDocId, navLoading, documentSearch, updateDocumentSearch, pullDocuments]);

    // Navigate to next document
    const goToNextDocument = useCallback(async () => {
        if (!nextDocId || navLoading) return;

        const currentNavVersion = ++navVersionRef.current;
        setNavLoading(true);

        try {
            if (nextDocId === '__LOAD_NEXT_PAGE__') {
                // Need to load next page — select first doc when it arrives
                pendingNavDirection.current = 'first';
                const currentOffset = documentSearch?.offset ?? 0;
                const currentLimit = documentSearch?.limit ?? 50;
                const newOffset = currentOffset + currentLimit;

                const newSearch = await updateDocumentSearch({
                    offset: newOffset,
                    search_origin: "nav"
                });
                await pullDocuments(newSearch);
                // Don't setNavLoading(false) here — the pending effect handles it
                return;
            } else {
                // Navigate to the next document in current list
                if (currentNavVersion !== navVersionRef.current) return; // Stale request

                const doc = await withApiErrorHandlerThrow<DocumentReadWReferences>(
                    () => getDocumentApi(nextDocId),
                    {titleQualifier: "Error Fetching Document"}
                );

                if (currentNavVersion !== navVersionRef.current) return; // Stale request
                _setDocument(doc);
            }
        } finally {
            if (currentNavVersion === navVersionRef.current && !pendingNavDirection.current) {
                setNavLoading(false);
            }
        }
    }, [nextDocId, navLoading, documentSearch, updateDocumentSearch, pullDocuments]);


    const deleteDocuments = async (docIds: string[]) => {
        const canDeleteDocs: DocumentRead[] = [];
        const cannotDeleteDocs: DocumentRead[] = [];
        const user: UserRead | null = await withApiErrorHandler(
            () => fetchOrStoreUser(),
            {titleQualifier: "Error Fetching User"}
        );
        if (user) {
            const userMemberships: UserMembership[] = user.groups_user_in;
            // We will check the permissions in the backend
            for (let i = 0; i < docIds.length; i++) {
                const docId = docIds[i];
                const doc = loadedDocMap.get(docId)!;

                const userInCurrentGroup = userMemberships.find((membership) => membership.group_id === currentGroup!.id);
                const userAdminOfCurrentGroup = userInCurrentGroup ? userInCurrentGroup.is_admin : false;
                if (userAdminOfCurrentGroup || doc.owner_id === user.id) {
                    canDeleteDocs.push(doc);
                } else {
                    cannotDeleteDocs.push(doc);
                }
            }
        }

        if (canDeleteDocs.length === 0) {
            Swal.fire(
                'Failed!',
                'You do not have permission to delete these documents. Make sure you are in the correct project, and have the correct permissions.',
                'error'
            );
            return;
        }


        const message = cannotDeleteDocs.length > 0 ?
            `You do not have permission to delete ${cannotDeleteDocs.length} of the ${docIds.length} documents requested. Do you still want to delete ${canDeleteDocs.length} documents, it will remove all instances of the documents?` :
            `Do you really want to delete ${canDeleteDocs.length} documents, it will remove all instances of the documents?`;

        const result = await Swal.fire({
            title: 'Are you sure?',
            text: message,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: ThemeColors.neutralDarkHex,
            cancelButtonColor: ThemeColors.colorContrastLightHex,
            confirmButtonText: 'Yes, delete them!'
        });
        if (result.isConfirmed) {
            const documentDeleteResults: DocumentDeleteResults | null = await withApiErrorHandler(
                () => deleteDocumentsApi(docIds),
                {titleQualifier: "Error Deleting Documents"}
            );
            if (!documentDeleteResults) {
                Swal.fire(
                    'Failed!',
                    'Failed to delete documents.',
                    'error'
                );
                return;
            } else {
                const removedDocIds = documentDeleteResults.doc_ids_deletes;
                setLoadedDocMap((prevDocuments) => {
                    const updatedDocuments = new Map(prevDocuments);
                    removedDocIds.forEach((id) => {
                        updatedDocuments.delete(id);
                    });
                    return updatedDocuments;
                });
                setDocIdsOrdered((prevOrder) => {
                    return prevOrder.filter((id) => !removedDocIds.includes(id));
                });

                Swal.fire(
                    'Deleted!',
                    'Your documents have been deleted.',
                    'success'
                );
            }

        } else if (result.dismiss === Swal.DismissReason.cancel) {
            // User canceled the deletion
            // Handle the cancellation if needed, or just do nothing
            console.log('Deletion cancelled');
        }


    }

    // Computed values for navigation availability
    const canGoPrev = prevDocId !== null;
    const canGoNext = nextDocId !== null;

    const contextValue: EnhancedDocumentsContextType = useMemo(() => ({
        docLoading,
        setDocLoading,
        documentSearch,
        updateDocumentSearch,
        pullDocuments,
        refreshDocumentField,
        getDocument,
        loadedDocMap,
        docTotalCount,
        docIdsOrdered,
        setDocIdsOrdered,

        selectedDocIds,
        setSelectedDocIds,

        deleteDocuments,
        document,
        setDocument,
        lastVisitedDocument,
        setLastVisitedDocument,

        // New navigation
        prevDocId,
        nextDocId,
        canGoPrev,
        canGoNext,
        goToPrevDocument,
        goToNextDocument,
        navLoading,

    }), [
        docLoading, documentSearch, loadedDocMap, docTotalCount, docIdsOrdered,
        selectedDocIds, document, lastVisitedDocument,
        prevDocId, nextDocId, navLoading,
        // Stable callbacks (included for completeness but won't change):
        pullDocuments, refreshDocumentField, getDocument, deleteDocuments,
        goToPrevDocument, goToNextDocument,
    ]);

    return (
        <EnhancedDocumentsContext.Provider value={contextValue}>
            {children}
        </EnhancedDocumentsContext.Provider>
    );
};

export const useEnhancedDocumentsContext = () => useContext(EnhancedDocumentsContext);
